<template>
  <transition name="unlock-fade">
    <div v-if="item" class="unlock-overlay" @click.self="onLater">
      <div class="unlock-card" :class="{ 'is-in': entered }">
        <!-- 光点装饰 -->
        <span class="spark spark-1"></span>
        <span class="spark spark-2"></span>
        <span class="spark spark-3"></span>
        <span class="spark spark-4"></span>
        <span class="spark spark-5"></span>
        <span class="spark spark-6"></span>

        <div class="unlock-tip">恭喜你！</div>
        <div class="unlock-title">解锁新成就</div>

        <!-- 徽章：先灰后彩 + 放大出现 -->
        <div class="unlock-badge-wrap">
          <div class="unlock-halo" :style="{ '--halo': item.levelColor || '#d9a327' }"></div>
          <AchievementBadge
            class="unlock-badge"
            :class="{ 'is-revealing': !revealed }"
            :level="item.level"
            :icon="item.icon"
            :locked="false"
            :size="116"
          />
        </div>

        <div class="unlock-name" :style="{ color: item.levelColor || '#d9a327' }">
          【{{ item.name }}】
        </div>
        <div class="unlock-desc">{{ item.description }}</div>
        <div v-if="item.peerName" class="unlock-peer">与好友「{{ item.peerName }}」共同达成</div>
        <div class="unlock-keep">继续保持！</div>

        <div v-if="moreCount > 0" class="unlock-more">还有 {{ moreCount }} 个成就已解锁</div>

        <div class="unlock-actions">
          <button type="button" class="btn-ghost" @click="onLater">稍后再看</button>
          <button type="button" class="btn-primary" @click="onView">查看成就</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import AchievementBadge from './AchievementBadge.vue'
import router from '@/router'
import { achievementStore, dismissUnlock, openAchievementsFromUnlock } from './achievementStore.js'

/**
 * 成就解锁提醒浮窗。
 *
 * 挂在 TaskBlockPage 顶层，读取全局 store 的 current 成就：
 * 徽章先以灰度小尺寸出现，随后「变彩色 + 放大」完成揭示动画。
 */
export default {
  name: 'AchievementUnlockDialog',
  components: { AchievementBadge },
  data() {
    return {
      entered: false,
      revealed: false,
      timers: []
    }
  },
  computed: {
    item() {
      return achievementStore.current
    },
    moreCount() {
      return achievementStore.queue.length
    }
  },
  watch: {
    item: {
      immediate: true,
      handler(val) {
        this.clearTimers()
        if (!val) return
        this.entered = false
        this.revealed = false
        // 下一帧触发过渡，让动画真正跑起来
        this.timers.push(setTimeout(() => {
          this.entered = true
        }, 20))
        // 徽章从灰到彩
        this.timers.push(setTimeout(() => {
          this.revealed = true
        }, 420))
      }
    }
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    clearTimers() {
      this.timers.forEach(t => clearTimeout(t))
      this.timers = []
    },
    onView() {
      // 先置「打开成就页 + 定位编码」，再跳转：
      // 若当前不在个人中心，跳转会新建 ProfilePage 并在 created 里消费该请求；
      // 若已经在个人中心，则由 ProfilePage 对 pendingOpen 的 watch 消费。
      openAchievementsFromUnlock()
      router.go('/taskblock/profile')
    },
    onLater() {
      dismissUnlock()
    }
  }
}
</script>

<style scoped>
.unlock-overlay {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(38, 51, 43, 0.35);
  padding: 20px;
}

.unlock-card {
  position: relative;
  width: 380px;
  max-width: 92vw;
  padding: 30px 28px 24px;
  background: var(--lp-surface, #fff);
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 1.25rem;
  box-shadow: 0 18px 46px rgba(52, 102, 68, 0.18);
  text-align: center;
  font-family: 'DynaPuff', 'OPPOSans', 'Microsoft YaHei', sans-serif;
  color: var(--lp-text, #26332b);
  transform: translateY(16px) scale(0.96);
  opacity: 0;
  transition: transform 0.32s cubic-bezier(0.2, 0.9, 0.3, 1.3), opacity 0.28s ease;
  overflow: hidden;
}

.unlock-card.is-in {
  transform: translateY(0) scale(1);
  opacity: 1;
}

.unlock-tip {
  font-size: 15px;
  font-weight: 700;
  color: var(--lp-text-2, #5f7268);
  letter-spacing: 0.08em;
}

.unlock-title {
  margin-top: 4px;
  font-size: 22px;
  font-weight: 800;
  color: var(--lp-text, #26332b);
}

.unlock-badge-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  margin: 8px 0 2px;
}

.unlock-halo {
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--halo, #d9a327) 0%, rgba(255, 255, 255, 0) 68%);
  opacity: 0;
  transition: opacity 0.5s ease;
}

.unlock-card.is-in .unlock-halo {
  opacity: 0.28;
  animation: halo-pulse 2.4s ease-in-out infinite;
}

.unlock-badge {
  position: relative;
  transform: scale(0.55);
  opacity: 0.35;
  transition: transform 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.4), opacity 0.5s ease;
}

.unlock-card.is-in .unlock-badge {
  transform: scale(1);
  opacity: 1;
}

/* 徽章先灰后彩 */
.unlock-badge.is-revealing {
  filter: grayscale(1);
}

.unlock-name {
  margin-top: 10px;
  font-size: 21px;
  font-weight: 800;
}

.unlock-desc {
  margin-top: 8px;
  font-size: 14px;
  color: var(--lp-text-2, #5f7268);
  line-height: 1.6;
}

.unlock-peer {
  margin-top: 6px;
  font-size: 13px;
  color: var(--lp-text-3, #94a59b);
}

.unlock-keep {
  margin-top: 10px;
  font-size: 14px;
  font-weight: 700;
  color: var(--lp-primary-dark, #2f7a4a);
}

.unlock-more {
  margin-top: 8px;
  font-size: 12px;
  color: var(--lp-text-3, #94a59b);
}

.unlock-actions {
  margin-top: 22px;
  display: flex;
  gap: 12px;
}

.unlock-actions button {
  flex: 1;
  height: 42px;
  border-radius: 0.75rem;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.btn-primary {
  background: var(--lp-primary, #4aa46a);
  border: 2px solid var(--lp-primary-dark, #2f7a4a);
  color: #fff;
}

.btn-primary:hover {
  background: var(--lp-primary-dark, #2f7a4a);
  transform: translateY(-1px);
}

.btn-ghost {
  background: var(--lp-surface, #fff);
  border: 2px solid var(--lp-border, #e3efe6);
  color: var(--lp-text-2, #5f7268);
}

.btn-ghost:hover {
  background: var(--lp-surface-2, #f7fbf8);
  color: var(--lp-primary-dark, #2f7a4a);
  transform: translateY(-1px);
}

/* 光点 */
.spark {
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--lp-primary, #4aa46a);
  opacity: 0;
}

.unlock-card.is-in .spark {
  animation: spark-fly 1.5s ease-out 0.35s 1 both;
}

.spark-1 { left: 14%; top: 34%; }
.spark-2 { left: 22%; top: 22%; background: #d9a327; animation-delay: 0.5s; }
.spark-3 { right: 16%; top: 30%; background: #d1487a; animation-delay: 0.42s; }
.spark-4 { right: 22%; top: 20%; background: #8264c4; animation-delay: 0.58s; }
.spark-5 { left: 26%; bottom: 26%; background: #d9a327; animation-delay: 0.62s; }
.spark-6 { right: 20%; bottom: 22%; background: #4aa46a; animation-delay: 0.48s; }

@keyframes spark-fly {
  0% { opacity: 0; transform: translate(0, 0) scale(0.4); }
  35% { opacity: 0.95; }
  100% { opacity: 0; transform: translate(var(--dx, 0), -26px) scale(1); }
}

@keyframes halo-pulse {
  0%, 100% { transform: scale(0.94); }
  50% { transform: scale(1.06); }
}

.unlock-fade-enter-active,
.unlock-fade-leave-active {
  transition: opacity 0.25s ease;
}

.unlock-fade-enter,
.unlock-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .unlock-card,
  .unlock-badge,
  .unlock-halo,
  .spark {
    transition: none !important;
    animation: none !important;
  }
}
</style>
